import { ComponentProps, useRef, useState } from "react";
import { SingleSelect } from "@bonliva-crm/ui";
import { useFormField } from "@clientUtils";
import { useLocale, useUpdateEffect } from "@hooks";
import { isFilterSelected } from "../utils/is-filter-selected";
import { FilterWithFavouriteProps } from "../utils/filters-record-types";
import { ControllableCollapsibleFavFilter } from "./_base/controllable-collapsible-fav-filter";
import { SingleValueChip } from "./_select/single-value-chip";
import { LegalBasis } from "@bonliva-crm/shared";

export type GDPRLegalBasisFilterData = LegalBasis;

type GDPRLegalBasisFilterProps = FilterWithFavouriteProps;
type Option = { label?: string; value: string | null };

export const GDPRLegalBasisFilter = (props: GDPRLegalBasisFilterProps) => {
  const { locale } = useLocale();
  const title = props.label ?? `${locale.GDPR}: ${locale.LegalBasis}`;
  const options = Object.keys(locale.legalBasisType).map((x) => ({
    label: locale.legalBasisType[x as LegalBasis],
    value: x
  }));

  const selectRef = useRef<HTMLInputElement>(null);
  const { field, fieldState, resetField, setFieldValue } =
    useFormField<LegalBasis | null>(props.name);
  const [expanded, setExpanded] = useState(fieldState.isDirty);

  useUpdateEffect(() => {
    if (expanded) {
      selectRef.current?.focus();
    }
  }, [expanded]);

  const value = {
    label: options.find((LabelValue) => LabelValue.value === field?.value)?.label,
    value: field?.value
  };

  const commonProps: ComponentProps<typeof SingleSelect<Option>> = {
    options,
    name: props.name,
    value,
    onChange: (selected) => {
      setFieldValue(selected?.value as LegalBasis, { shouldDirty: true });
    },
    hideSelectedOptions: false,
    controlShouldRenderValue: false,
    placeholder: title,
    isSearchable: false,
    maxMenuHeight: 600,
    showBadge: isFilterSelected(field?.value),
    optionType: "radio",
    isClearable: isFilterSelected(field?.value),
    onClear: () => setFieldValue(null, { shouldDirty: true })
  };

  if (props.setFavorite) {
    return (
      <ControllableCollapsibleFavFilter
        title={title}
        isDirty={fieldState.isDirty}
        isClearable={fieldState.isDirty}
        isExpanded={expanded}
        onClear={resetField}
        favorite={props.favorite}
        onSetFavorite={props.setFavorite}
        onExpand={() => setExpanded(!expanded)}
      >
        <SingleSelect<Option>
          {...commonProps}
          selectRef={selectRef}
          isClearable={false}
          controlShouldRenderValue
          openMenuOnFocus
          menuPortalTarget={document.body}
          showBadge={false}
          placeholder={locale.SelectValue}
          menuPlacement="auto"
          styleProps={{
            menu: { showBorder: true },
            valueContainer: { padding: 0 },
            menuPortal: { zIndex: 9999 }
          }}
          components={{
            SingleValue: (p) => <SingleValueChip {...p} onDelete={resetField} />
          }}
        />
      </ControllableCollapsibleFavFilter>
    );
  }

  return (
    <SingleSelect<Option>
      {...commonProps}
      styleProps={{
        control: { showBorder: true },
        menu: { showBorder: true }
      }}
    />
  );
};
